import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, FileSpreadsheet, Loader2, UploadCloud } from 'lucide-react';
import { readCatalogFile, reviewCatalog, splitCatalogBatches } from './catalogImport.js';
import { Button, Header, StatusBadge } from './ui.jsx';

const csvValue = (value) => {
  const raw = String(value ?? '');
  const safe = /^[=+@-]/.test(raw) ? "'" + raw : raw;
  return '"' + safe.replace(/"/g,'""') + '"';
};
function exportIssues(rows) {
  const output = [
    ['Linha','SKU original','Produto','Código de barras','Estoque original','Motivos de revisão'].map(csvValue).join(';'),
    ...rows.map((row) => [
      row.line,row.payload.sku,row.payload.name,
      row.payload.metadata.legacy_import.original_barcode,
      row.payload.metadata.legacy_import.original_stock,
      row.issues.join(' | '),
    ].map(csvValue).join(';')),
  ].join('\r\n');
  const url = URL.createObjectURL(new Blob(['\uFEFF', output],{type:'text/csv;charset=utf-8'}));
  const a = document.createElement('a');
  a.href=url; a.download='smartpdv-pendencias-importacao.csv'; a.click();
  URL.revokeObjectURL(url);
}
async function sha256(file) {
  const bytes = new Uint8Array(await crypto.subtle.digest('SHA-256', await file.arrayBuffer()));
  return [...bytes].map((n)=>n.toString(16).padStart(2,'0')).join('');
}
export default function CatalogImport({ data, organization, location, capabilities, onCommit, onDone, isDemo=false }) {
  const [review,setReview]=useState(null);
  const [filename,setFilename]=useState('');
  const [reading,setReading]=useState(false);
  const [importing,setImporting]=useState(false);
  const [error,setError]=useState('');
  const [acknowledged,setAcknowledged]=useState(false);
  const [progress,setProgress]=useState(null);
  const canImport=Boolean(capabilities?.catalog&&capabilities?.inventory&&!isDemo);
  async function onFile(file) {
    if (!file) return;
    setReading(true); setReview(null); setError(''); setAcknowledged(false); setProgress(null);
    try {
      const [raw, hash] = await Promise.all([readCatalogFile(file), sha256(file)]);
      const report = reviewCatalog(raw, data.products, file.name);
      report.rows.forEach((row)=>{row.payload.metadata.legacy_import.source_hash=hash;});
      setReview(report);
      setFilename(file.name);
    } catch (e) {setError(String(e?.message||e));}
    finally {setReading(false);}
  }
  async function confirmImport() {
    if (!review?.ready.length || !acknowledged || !canImport || importing) return;
    setImporting(true); setError(''); setProgress({done:0,total:review.ready.length,created:0,skipped:0});
    try {
      const batches=splitCatalogBatches(review.ready,30);
      let done=0,created=0,skipped=0;
      for(const batch of batches) {
        const result=await onCommit(batch);
        done+=batch.length; created+=Number(result.created||0);skipped+=Number(result.already_imported||0);
        setProgress({done,total:review.ready.length,created,skipped});
      }
      setAcknowledged(false);
      await onDone();
    } catch(e) {
      setError('Importação interrompida: '+String(e?.message||e)+'. Os lotes concluídos foram preservados. Reenvie o arquivo para revisar e continuar sem duplicar.');
    } finally {setImporting(false);}
  }
  return <div className="admin" data-testid="smartpdv-catalog-import">
    <Header eyebrow="IMPLANTAÇÃO SMARTPDV" title="Importar catálogo"
      description="Traga os produtos do sistema anterior com validação, conferência humana e registro de auditoria."/>
    <section className="panel importPanel">
      <h2>1. Enviar base de produtos</h2>
      <p>Formatos XLSX ou CSV até 10 MB. A análise é local no navegador; nada é gravado antes da sua aprovação.</p>
      <label className="importUpload">
        {reading?<Loader2 className="spin" size={22}/>:<UploadCloud size={22}/>}
        <span>{reading?'Lendo e verificando...':'Selecionar arquivo Excel ou CSV'}</span>
        <input type="file" accept=".xlsx,.csv" disabled={reading||importing} onChange={(event)=>{onFile(event.target.files?.[0]);event.currentTarget.value='';}}/>
      </label>
      <small>Importação por loja: <strong>{location?.name||'Sem loja ativa'}</strong> · empresa: {organization?.name||'—'}.</small>
      {error&&<p className="error" role="alert">{error}</p>}
      {!canImport&&<p className="muted">A prévia está disponível, mas gravar dados requer permissão de catálogo e estoque, acesso real à empresa e ambiente conectado.</p>}
    </section>
    {review&&<>
      <section className="panel importPanel">
        <h2>2. Prévia da importação</h2>
        <p>Arquivo: {filename}. Registros conflitantes não serão enviados ao banco.</p>
        <div className="importStats">
          <div><b>{review.counts.total}</b><span>Registros</span></div>
          <div><b>{review.counts.ready}</b><span>Prontos</span></div>
          <div><b>{review.counts.blocked}</b><span>Revisão obrigatória</span></div>
          <div><b>{review.counts.zero}</b><span>Estoque zero</span></div>
          <div><b>{review.counts.negative}</b><span>Estoque negativo</span></div>
        </div>
        {review.blocked.length>0&&<div className="importWarning">
          <AlertTriangle size={18}/>
          <span>Linhas com código repetido, estoque negativo, identificadores incorretos ou dados fiscais inválidos permanecem pendentes; nenhuma será corrigida automaticamente.</span>
        </div>}
        <div className="panelTitle">
          <div><h3>Pendências para revisar</h3><p>Exibindo até 80 linhas; exporte a lista completa para conciliação.</p></div>
          <Button kind="ghost" size="small" disabled={!review.blocked.length} onClick={()=>exportIssues(review.blocked)}>Exportar pendências CSV</Button>
        </div>
        <div className="importRows">
          {review.blocked.slice(0,80).map((row)=><div key={row.line} className="importRow">
            <div><strong>{row.payload.name||'Produto sem nome'}</strong><small>Linha {row.line} · SKU {row.payload.sku||'vazio'} · saldo {row.payload.opening_qty}</small></div>
            <small>{row.issues.join(' · ')}</small>
          </div>)}
          {!review.blocked.length&&<p><CheckCircle2 size={16}/> Nenhuma pendência identificada neste arquivo.</p>}
        </div>
      </section>
      <section className="panel importPanel">
        <h2>3. Aprovar e importar</h2>
        <p>O SmartPDV somente cria novos produtos prontos, lança estoque inicial positivo no histórico e ignora itens já importados deste mesmo arquivo. Nunca sobrescreve cadastros existentes.</p>
        <label className="importConfirm">
          <input type="checkbox" checked={acknowledged} onChange={(event)=>setAcknowledged(event.target.checked)} disabled={importing}/>
          Conferi os dados e autorizo importar os {review.counts.ready} produtos aprovados para esta empresa e loja.
        </label>
        {progress&&<div role="status" className="importProgress">
          <strong>{progress.done} / {progress.total} processados</strong>
          <span>Novos: {progress.created} · já importados: {progress.skipped}</span>
          <progress max={progress.total||1} value={progress.done}/>
        </div>}
        <div className="headerActions">
          <Button disabled={!acknowledged||!canImport||!review.ready.length||!navigator.onLine||importing} onClick={confirmImport}>
            {importing?<Loader2 size={17} className="spin"/>:<FileSpreadsheet size={17}/>}
            {importing?'Importando em lotes...':'Importar produtos aprovados'}
          </Button>
          <StatusBadge tone="warning">Importação fiscal não homologada</StatusBadge>
        </div>
      </section>
    </>}
  </div>;
}
